import * as MuiIcons from "@mui/icons-material";
import { ComponentType } from "react";
import { useFormContext } from "react-hook-form";

import {
  InputError,
  InputLabel,
  TextInput,
} from "@/components/global/form/inputs";
import {
  BaseInputStyleProps,
  InputErrorStyleProps,
  InputLabelStyleProps,
  InputWrapperStyleProps,
} from "@/components/global/form/inputs/styles";
import { FormFieldLayout } from "@/components/global/form/layouts/form-field-layout";

interface InputEvents {
  onClick?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

interface FormInputEvents {
  label?: InputEvents;
  input?: InputEvents;
}

export interface FormInputProps<T = any> {
  name: string;
  label: React.ReactNode;
  required?: boolean;
  error?: string;
  isValid?: boolean;
  placeholder?: string;
  dataCy?: string;
  disabled?: boolean;
  readOnly?: boolean;
  defaultValue?: string;
  cursorOnHover?: "pointer" | "default" | "not-allowed";
  component?: ComponentType<any>;
  componentProps?: Omit<T, keyof FormInputProps>;
  events?: FormInputEvents;
  styles?: {
    wrapper?: InputWrapperStyleProps;
    error?: InputErrorStyleProps;
    label?: InputLabelStyleProps;
    input?: BaseInputStyleProps;
    layout?: {
      className?: string;
    };
  };
  icon?: {
    name: keyof typeof MuiIcons;
    position?: "left" | "right";
    size?: number;
    className?: string;
    onClick?: () => void;
    cursorOnHover?: "pointer" | "default" | "not-allowed";
  };
  isNew?: boolean;
}

/**
 * Complete form field component with label, input and error message
 * @param {string} name - Field name (used for react-hook-form)
 * @param {string} label - Field label
 * @param {boolean} [required] - Whether the field is required
 * @param {string} [error] - Error message to display
 * @param {boolean} [isValid] - Whether the field is valid
 * @param {string} [placeholder] - Input placeholder
 * @param {string} [dataCy] - Data-cy attribute for testing
 * @param {boolean} [disabled] - Whether the field is disabled
 * @param {boolean} [readOnly] - Whether the field is read-only
 * @param {string} [cursorOnHover] - Cursor style on hover
 * @param {ComponentType} [component] - Custom input component (defaults to TextInput)
 * @param {Object} [styles] - Custom styles for each part of the component
 * @param {InputWrapperStyleProps} [styles.wrapper] - Styles for the input wrapper
 * @param {InputErrorStyleProps} [styles.error] - Styles for the error message
 * @param {InputLabelStyleProps} [styles.label] - Styles for the label
 * @param {BaseInputStyleProps} [styles.input] - Styles for the input
 * @param {Object} [icon] - Icon configuration for the input
 * @param {string} [icon.name] - Icon name
 * @param {string} [icon.position] - Icon position
 * @param {number} [icon.size] - Icon size
 * @param {string} [icon.className] - Icon class name
 * @param {function} [icon.onClick] - Icon click handler
 * @param {string} [icon.cursorOnHover] - Cursor style on hover
 */
export const FormInput = <T = any,>({
  name,
  label,
  required,
  error,
  isValid,
  placeholder,
  dataCy,
  disabled,
  readOnly,
  cursorOnHover,
  styles,
  events,
  defaultValue,
  component: InputComponent = TextInput,
  componentProps = {} as Omit<T, keyof FormInputProps>,
  icon,
}: FormInputProps<T>) => {
  const { register } = useFormContext();

  return (
    <FormFieldLayout className={styles?.layout?.className}>
      <InputLabel
        htmlFor={name}
        label={label}
        required={required}
        styles={styles?.label}
        onClick={events?.label?.onClick}
      />
      <InputComponent
        {...register(name)}
        error={error}
        isValid={isValid}
        placeholder={placeholder}
        required={required}
        dataCy={dataCy}
        disabled={disabled}
        readOnly={readOnly}
        cursorOnHover={cursorOnHover}
        wrapperStyles={styles?.wrapper}
        styles={styles?.input}
        events={events?.input}
        icon={icon}
        defaultValue={defaultValue ? defaultValue : undefined}
        {...componentProps}
      />
      {error && <InputError error={error} styles={styles?.error} />}
    </FormFieldLayout>
  );
};
